import Checkbox from '@mui/material/Checkbox'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import {
  KeyValue,
  NotificationMethod,
  NotificationTemplateDataFragment,
  NotificationTemplateDefaultInput,
  NotificationType,
} from '../../generated/graphql'
import { useUserContext } from '../../providers/UserContext'

const useStyles = makeStyles()(() => {
  return {
    table: {
      marginTop: 0,
    },
    headerBlock: {
      display: 'flex',
      flexDirection: 'column',
    },
    tooltip: {
      fontSize: '0.8rem',
      padding: '8px 12px',
      maxWidth: '400px',
      lineHeight: '1.4',
    },
  }
})

interface Props {
  templates: NotificationTemplateDataFragment[]
  types: KeyValue[]
  methods: KeyValue[]
  defaultTemplatesIds: string[]
  /** when true (default), only country admins can change defaults. when false (e.g. Edit Event), any admin can toggle Use default? and select template. */
  restrictByBranchAdmin?: boolean

  editTemplate(data: NotificationTemplateDefaultInput): void
}

const getNotificationTypeDescription = (type: string): string => {
  const descriptions: { [key: string]: string } = {
    ACCOUNT_PASSWORD_RESET: 'Sent when a user requests a password reset',
    ACCOUNT_USER_CREATED: 'Sent when a new user account is created',
    ACCOUNT_EMAIL_CHANGED: 'Sent when a user changes their email address',
    ATTENDEE_ATTENDEE_REGISTERED:
      'Sent to confirm registration when accepted the invitation or registered via Patient Portal calendar.',
    ATTENDEE_ATTENDEE_INVITED: 'Sent when an attendee is invited to an event via Attendance Registration',
    ATTENDEE_ATTENDEE_INVITED_ORGANIZER_INFORM: 'Notification to organizer when an attendee is invited',
    ATTENDEE_ATTENDEE_INVITED_ACCEPT_ORGANIZER_INFORM: 'Notification to organizer when an invited attendee accepts',
    ATTENDEE_ATTENDEE_REJECTED:
      'Sent when an attendee decline an event invitation, canceled via Patient Portal or deleted via Patient Registration.',
    ATTENDEE_ATTENDEE_REJECTED_ORGANIZERS:
      'Notification to organizers when an attendee decline or cancels via the Patient Portal.',
    ATTENDEE_EVENT_FINISHED:
      'Sent when the event is marked as finished, but only for patients who are marked as attended in Attendance Registration.',
    ATTENDEE_EVENT_CANCELLED: 'Sent when an event is cancelled/deleted',
    ATTENDEE_EVENT_DATE_CHANGED: 'Sent when event date or time is modified',
    ATTENDEE_EVENT_REMINDER: 'Reminder',
    ATTENDEE_EVENT_CUSTOM: 'Custom notification',
    ATTENDEE_INVITATION_ACCEPTANCE_REMINDER: 'Reminder for unaccepted invitations',
  }
  return descriptions[type] || 'Notification type description not available'
}

const TemplateSettingsTable: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const user = useUserContext()
  const isRootBranchUser = !!user.viewer?.organizationBranch && !user.viewer.organizationBranch.parentId
  const restrictByBranchAdmin = props.restrictByBranchAdmin !== false

  const { templates, types, methods, defaultTemplatesIds } = props
  const headers: string[] = [t('Type'), t('Default template'), t('Use default?')]

  return (
    <div>
      {methods.map((methodObj) => {
        const methodValue = methodObj.value
        const methodKey = methodObj.key
        return (
          <React.Fragment key={methodKey}>
            <Toolbar>
              <Typography variant="h6" id="tableTitle">
                {methodValue}
              </Typography>
            </Toolbar>
            <Table size="small" aria-labelledby="tableTitle" className={classes.table}>
              <TableHead>
                <TableRow>
                  {headers.map((h, idx) => (
                    <TableCell key={'h' + idx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {types
                  .filter(
                    (typeObj) =>
                      !['ACCOUNT_PASSWORD_RESET', 'ACCOUNT_USER_CREATED', 'ACCOUNT_EMAIL_CHANGED'].includes(
                        typeObj.key,
                      ),
                  )
                  .map((typeObj) => {
                    const typeValue = typeObj.value
                    const typeKey = typeObj.key
                    const activeTemplates = templates.filter(
                      (template) => template.active && template.type === typeKey && template.method === methodKey,
                    )
                    const defaultTemplate = activeTemplates.find((template) =>
                      defaultTemplatesIds.includes(template.id),
                    )
                    return (
                      <TableRow key={methodKey + typeKey}>
                        <TableCell>
                          <Tooltip
                            title={getNotificationTypeDescription(typeKey)}
                            arrow
                            placement="top"
                            classes={{ tooltip: classes.tooltip }}
                          >
                            <span>{typeValue}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Select
                            native
                            variant="standard"
                            value={defaultTemplate && defaultTemplate.id}
                            disabled={!defaultTemplate || (restrictByBranchAdmin && isRootBranchUser)}
                            onChange={async (event) => {
                              const newDefaultId = event.target.value
                              if (!defaultTemplate || newDefaultId !== defaultTemplate.id) {
                                const newDefaultTemplate: NotificationTemplateDataFragment | undefined =
                                  activeTemplates.find((tpl) => tpl.id === newDefaultId)
                                if (newDefaultTemplate) {
                                  await props.editTemplate({
                                    method: methodKey as NotificationMethod,
                                    type: typeKey as NotificationType,
                                    id: newDefaultTemplate.id,
                                  })
                                }
                              }
                            }}
                          >
                            {activeTemplates.map((template) => (
                              <option key={template.id} value={template.id}>
                                {template.title}
                              </option>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              restrictByBranchAdmin && isRootBranchUser && !defaultTemplate
                                ? t(
                                    'Custom default templates can only be set by country administrators for their specific country branch.',
                                  )
                                : ''
                            }
                            arrow
                            placement="top"
                            classes={{ tooltip: classes.tooltip }}
                          >
                            <span>
                              <Checkbox
                                checked={!defaultTemplate}
                                onChange={(_event: object, checked: boolean) => {
                                  props.editTemplate({
                                    type: typeKey as NotificationType,
                                    method: methodKey as NotificationMethod,
                                    id: checked ? undefined : activeTemplates[0].id,
                                  })
                                }}
                                disabled={
                                  !activeTemplates.length ||
                                  (restrictByBranchAdmin && isRootBranchUser && !defaultTemplate)
                                }
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default TemplateSettingsTable
